import React from 'react';
import {
  ShieldCheck, ShieldAlert, Shield, ShieldX, AlertTriangle, Clock,
  UserX, ServerCrash,
} from 'lucide-react';

/* -------------------------------------------------------------------------- */
/*                           Certification status                             */
/* -------------------------------------------------------------------------- */

/**
 * Maps a (certStatus, preflightReady, hasNccIssue) tuple to a business-friendly
 * label, color tone, and icon. Output is rendered as a compact pill.
 */
export const FneCertStatusBadge = ({ certStatus, preflightReady, hasNccIssue, hasOtherBlock }) => {
  let label = 'À certifier';
  let cls = 'bg-amber-50 text-amber-700 border-amber-200';
  let Icon = Shield;

  if (certStatus === 'certified') {
    label = 'Certifiée';
    cls = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    Icon = ShieldCheck;
  } else if (certStatus === 'failed') {
    label = 'Échec certification';
    cls = 'bg-rose-50 text-rose-700 border-rose-200';
    Icon = ShieldX;
  } else if (certStatus === 'refunded') {
    label = 'Annulée';
    cls = 'bg-slate-50 text-slate-600 border-slate-200';
    Icon = ShieldAlert;
  } else if (!preflightReady && (hasNccIssue || hasOtherBlock)) {
    label = 'Bloquée';
    cls = 'bg-rose-50 text-rose-700 border-rose-200';
    Icon = ShieldAlert;
  }

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-xs font-medium ${cls}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
};

/* -------------------------------------------------------------------------- */
/*                              Priority badge                                */
/* -------------------------------------------------------------------------- */

const dayDiff = (iso) => {
  if (!iso) return 0;
  try {
    const d = new Date(iso);
    return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
  } catch { return 0; }
};

export const FnePriorityBadge = ({ item }) => {
  const cert = item?.fne?.status;
  if (cert === 'certified' || cert === 'refunded') return null;

  const issues = item?.preflight?.issues || [];
  const nccIssue = issues.some((i) => /ncc|compteContribuable/i.test(`${i.field}${i.message}`));
  const age = dayDiff(item?.createdAt);

  if (nccIssue) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200 text-[10px] font-medium">
        <UserX className="w-2.5 h-2.5" /> Bloqué client
      </span>
    );
  }
  if (!item?.preflight?.ready && issues.length > 0) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-medium">
        <ServerCrash className="w-2.5 h-2.5" /> Bloqué système
      </span>
    );
  }
  if (age >= 7) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-rose-50 text-rose-700 border border-rose-200 text-[10px] font-medium">
        <AlertTriangle className="w-2.5 h-2.5" /> Urgent
      </span>
    );
  }
  if (age >= 2) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-medium">
        <Clock className="w-2.5 h-2.5" /> À traiter aujourd&apos;hui
      </span>
    );
  }
  return null;
};
